import React, { useState, useEffect, useCallback } from "react";
import { Plus, Search, Edit2, Trash2, Loader2, Image as ImageIcon } from "lucide-react";
import Button from "../../components/ui/Button";
import Pagination from "../../components/ui/Pagination";
import SortableHeader from "../../components/ui/SortableHeader";
import Modal from "../../components/ui/Modal";
import ImageModal from "../../components/ui/ImageModal";
import ConsumoForm from "./ConsumoForm";
import { useToast } from "../../../context/ToastContext";
import { useAuth } from "../../../context/AuthContext";
import { AdminService } from "../../../application/services/AdminService";
import DashboardLayout from "../../layouts/DashboardLayout";
import API_URL from "../../../config";

const ConsumosPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [consumos, setConsumos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    key: "fecha",
    direction: "DESC",
  });

  const [showModal, setShowModal] = useState(false);
  const [selectedConsumo, setSelectedConsumo] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const toast = useToast();

  const fetchData = useCallback(
    async (
      currentPage = 1,
      search = searchTerm,
      currentLimit = limit,
      currentSort = sortConfig,
    ) => {
      setLoading(true);
      try {
        let result;
        result = await AdminService.getAllConsumos(
          currentPage,
          currentLimit,
          search,
          currentSort.key,
          currentSort.direction,
        );

        setConsumos(result.items || result.consumos || []);
        setTotalPages(result.totalPages || 1);
        setTotalItems(result.total || result.count || 0);
        setPage(currentPage);
      } catch (error) {
        console.error("Error fetching consumos:", error);
        toast.error("Error al cargar consumos");
        setConsumos([]);
      } finally {
        setLoading(false);
      }
    },
    [searchTerm, limit, sortConfig, toast],
  );

  useEffect(() => {
    if (!authLoading) {
      fetchData(1, searchTerm, limit, sortConfig);
    }
  }, [authLoading, user, fetchData, searchTerm, limit, sortConfig]);

  const handleSort = (key) => {
    let direction = "ASC";
    if (sortConfig.key === key && sortConfig.direction === "ASC") {
      direction = "DESC";
    }
    const newSort = { key, direction };
    setSortConfig(newSort);
    // fetchData will be called by the useEffect hook due to sortConfig change
  };

  const handleItemsPerPageChange = (newLimit) => {
    setLimit(newLimit);
    // fetchData will be called by the useEffect hook due to limit change
  };

  const handleOpenCreate = () => {
    setSelectedConsumo(null);
    setShowModal(true);
  };

  const handleOpenEdit = (consumo) => {
    setSelectedConsumo(consumo);
    setShowModal(true);
  };

  const handleDelete = (consumo) => {
    setSelectedConsumo(consumo);
    setShowDeleteConfirm(true);
  };

  const handleSuccess = () => {
    setShowModal(false);
    fetchData(page, searchTerm, limit, sortConfig);
  };

  const confirmDelete = async () => {
    try {
      await AdminService.deleteConsumo(selectedConsumo.id_consumo);
      toast.success("Consumo eliminado exitosamente");
      fetchData(page, searchTerm, limit, sortConfig);
    } catch (error) {
      console.error("Error deleting consumo:", error);
      toast.error("Error al eliminar consumo");
    } finally {
      setShowDeleteConfirm(false);
      setSelectedConsumo(null);
    }
  };

  const handleOpenImage = (consumo) => {
    setSelectedImage(`${API_URL}/images/consumos/${consumo.foto_evidencia}`);
    setShowImageModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    // Append UTC to timezone options so midnight UTC doesn't shift locally
    return new Date(dateStr).toLocaleDateString("es-EC", { timeZone: "UTC" });
  };

  return (
    <DashboardLayout title="Gestión de Consumos">
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
              Consumos
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Registra y administra las lecturas de consumo de agua.
            </p>
          </div>
          <Button
            onClick={handleOpenCreate}
            className="flex items-center gap-2 bg-blue-600 shadow-sm hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Registrar Lectura
          </Button>
        </div>

        {/* Buscador */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 mb-6 flex items-center">
          <Search className="w-5 h-5 text-gray-400 mr-3" />
          <input
            type="text"
            placeholder="Buscar por código de medidor o cliente..."
            className="flex-1 outline-none text-gray-600 placeholder-gray-400"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              // fetchData will be called by the useEffect hook due to searchTerm change
            }}
          />
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <SortableHeader
                    label="Medidor"
                    field="codigo"
                    currentSort={sortConfig.key}
                    currentOrder={sortConfig.direction}
                    onSort={handleSort}
                  />
                  <th className="px-4 py-3 text-left text-xs uppercase font-bold text-gray-500 tracking-wider min-w-[200px]">
                    Cliente
                  </th>
                  <SortableHeader
                    label="Lect. Anterior"
                    field="lectura_anterior"
                    currentSort={sortConfig.key}
                    currentOrder={sortConfig.direction}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Lect. Actual"
                    field="lectura_actual"
                    currentSort={sortConfig.key}
                    currentOrder={sortConfig.direction}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Consumo"
                    field="consumo"
                    currentSort={sortConfig.key}
                    currentOrder={sortConfig.direction}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Fecha Lectura"
                    field="fecha"
                    currentSort={sortConfig.key}
                    currentOrder={sortConfig.direction}
                    onSort={handleSort}
                  />
                  <th className="px-4 py-3 text-center text-xs uppercase font-bold text-gray-500 tracking-wider">
                    Reconexión
                  </th>
                  <th className="px-4 py-3 text-right text-xs uppercase font-bold text-gray-500 tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      <div className="flex justify-center items-center">
                        <Loader2 className="w-6 h-6 animate-spin mr-2" />
                        Cargando consumos...
                      </div>
                    </td>
                  </tr>
                ) : consumos.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      No se encontraron consumos
                    </td>
                  </tr>
                ) : (
                  consumos.map((consumo) => (
                    <tr
                      key={consumo.id_consumo}
                      className="hover:bg-gray-50 transition-colors border-b border-gray-100"
                    >
                      <td className="px-4 py-1.5 text-sm font-mono font-medium text-gray-900">
                        {consumo.codigo_medidor ||
                          consumo.medidor?.codigo_medidor ||
                          "-"}
                      </td>
                      <td className="px-4 py-1.5 text-sm text-gray-700 font-medium whitespace-nowrap">
                        {consumo.cliente
                          ? `${consumo.cliente.nombres} ${consumo.cliente.apellidos}`
                          : "-"}
                      </td>
                      <td className="px-4 py-1.5 text-sm text-gray-600 text-center">
                        {consumo.lectura_anterior}
                      </td>
                      <td className="px-4 py-1.5 text-sm text-gray-600 text-center">
                        {consumo.lectura_actual}
                      </td>
                      <td className="px-4 py-1.5 text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                          {consumo.consumo ||
                            consumo.lectura_actual -
                              consumo.lectura_anterior}{" "}
                          m³
                        </span>
                      </td>
                      <td className="px-4 py-1.5 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(consumo.fecha_lectura)}
                      </td>
                      <td className="px-4 py-1.5 text-center">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${
                            consumo.reconexion === "SI"
                              ? "bg-amber-100 text-amber-800"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {consumo.reconexion}
                        </span>
                      </td>
                      <td className="px-4 py-1.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {consumo.foto_evidencia && (
                            <button
                              onClick={() => handleOpenImage(consumo)}
                              className="text-gray-400 hover:text-green-600 transition-colors p-1"
                              title="Ver Evidencia"
                            >
                              <ImageIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleOpenEdit(consumo)}
                            className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                            title="Editar"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(consumo)}
                            className="text-gray-400 hover:text-red-600 transition-colors p-1"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={limit}
            onPageChange={(p) => fetchData(p, searchTerm, limit, sortConfig)}
            onItemsPerPageChange={handleItemsPerPageChange}
            loading={loading}
          />
        </div>

        {/* Modal Crear/Editar */}
        {showModal && (
          <ConsumoForm
            onClose={() => setShowModal(false)}
            onSuccess={handleSuccess}
            initialData={selectedConsumo}
          />
        )}

        {/* Confirmación Eliminar */}
        <Modal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          title="Eliminar Consumo"
          size="sm"
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancelar
              </Button>
              <Button
                className="bg-red-600 hover:bg-red-700"
                onClick={confirmDelete}
              >
                Eliminar
              </Button>
            </>
          }
        >
          <div className="text-gray-600">
            <p>¿Estás seguro de que deseas eliminar este consumo?</p>
            <p className="text-sm mt-2 text-red-500">
              Esta acción no se puede deshacer.
            </p>
          </div>
        </Modal>

        {/* Modal Ver Imagen */}
        <ImageModal
          isOpen={showImageModal}
          onClose={() => setShowImageModal(false)}
          imageUrl={selectedImage}
        />
      </div>
    </DashboardLayout>
  );
};

export default ConsumosPage;
